// components/ui/Skeleton.tsx
import { cn } from '../../lib/utils';

interface SkeletonProps {
  className?: string;
}

interface SkeletonTextProps extends SkeletonProps {
  lines?: number;
}

interface SkeletonCircleProps extends SkeletonProps {
  size?: number;
}

interface SkeletonRectangleProps extends SkeletonProps {
  width?: string | number;
  height?: string | number;
}

const baseClass = 'animate-pulse bg-gray-200 rounded';

export function Skeleton({ className }: SkeletonProps) {
  return <div className={cn(baseClass, className)} />;
}

export function SkeletonText({ lines = 1, className }: SkeletonTextProps) {
  return (
    <div className={cn('space-y-2', className)}>
      {Array.from({ length: lines }).map((_, i) => (
        <div
          key={i}
          className={cn(
            baseClass,
            'h-4',
            i === lines - 1 ? 'w-2/3' : 'w-full'
          )}
        />
      ))}
    </div>
  );
}

export function SkeletonCircle({ size = 40, className }: SkeletonCircleProps) {
  return (
    <div
      className={cn(baseClass, 'rounded-full', className)}
      style={{ width: size, height: size }}
    />
  );
}

export function SkeletonRectangle({
  width = '100%',
  height = 100,
  className
}: SkeletonRectangleProps) {
  return (
    <div
      className={cn(baseClass, className)}
      style={{
        width: typeof width === 'number' ? `${width}px` : width,
        height: typeof height === 'number' ? `${height}px` : height,
      }}
    />
  );
}

// Pre-built skeleton patterns
export function SkeletonCard({ className }: SkeletonProps) {
  return (
    <div className={cn('bg-white rounded-lg border border-gray-200 overflow-hidden', className)}>
      <SkeletonRectangle height={160} />
      <div className="p-4 space-y-3">
        <SkeletonText />
        <SkeletonText lines={2} />
      </div>
    </div>
  );
}

export function SkeletonListItem({ className }: SkeletonProps) {
  return (
    <div className={cn('flex items-center gap-4 p-4', className)}>
      <SkeletonCircle size={48} />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-1/3" />
        <Skeleton className="h-3 w-1/2" />
      </div>
    </div>
  );
}
